<template>
  <div>
    <CalendarHeader
      :current-month="currentMonth"
      :campaigns="uniqueCampaigns"
      @prev="prevMonth"
      @next="nextMonth"
      @today="goToday"
      @filter="onFilterCampaign" />

    <CalendarGrid
      :current-month="currentMonth"
      :posts="filteredPosts"
      :campaign-colors="campaignColors"
      @drop="onDrop"
      @click-post="onClickPost"
      @click-day="onClickDay" />

    <UnscheduledTray
      :posts="unscheduledPosts"
      :campaign-colors="campaignColors"
      @drop="onUnschedule"
      @click-post="onClickPost" />

    <GbpPostDetail
      v-if="selectedPost"
      :post="selectedPost"
      @close="selectedPost = null"
      @edit="onEditPost"
      @regenerate="onRegeneratePost"
      @updated="fetchPosts" />

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { addMonths, subMonths, startOfMonth, endOfMonth, subWeeks, addWeeks, format } from 'date-fns'
import { getLocationGbpPosts, updateGbpPost, regenerateGbpPost } from '~/javascript/api/gbp_posts'
import CalendarHeader from './calendar_header.vue'
import CalendarGrid from './calendar_grid.vue'
import UnscheduledTray from './unscheduled_tray.vue'
import GbpPostDetail from '~/javascript/components/gbp_posts/detail.vue'
import GbpPostEditor from '~/javascript/components/gbp_posts/editor.vue'
import QuickCreateForm from './quick_create_form.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'

const CAMPAIGN_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', darkBg: 'dark:bg-blue-900', darkText: 'dark:text-blue-200' },
  { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500', darkBg: 'dark:bg-emerald-900', darkText: 'dark:text-emerald-200' },
  { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500', darkBg: 'dark:bg-purple-900', darkText: 'dark:text-purple-200' },
  { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500', darkBg: 'dark:bg-amber-900', darkText: 'dark:text-amber-200' },
  { bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500', darkBg: 'dark:bg-rose-900', darkText: 'dark:text-rose-200' },
  { bg: 'bg-cyan-100', text: 'text-cyan-800', dot: 'bg-cyan-500', darkBg: 'dark:bg-cyan-900', darkText: 'dark:text-cyan-200' }
]

const props = defineProps({
  locationId: { type: String, required: true }
})

const currentMonth = ref(new Date())
const posts = ref([])
const selectedPost = ref(null)
const filterCampaignId = ref('')
const slidePanel = ref(null)

const campaignColors = computed(() => {
  const map = new Map()
  const ids = [...new Set(posts.value.map((p) => p.gbp_campaign_id))]
  ids.forEach((id, i) => {
    map.set(id, CAMPAIGN_COLORS[i % CAMPAIGN_COLORS.length])
  })
  return map
})

const uniqueCampaigns = computed(() => {
  const seen = new Map()
  posts.value.forEach((post) => {
    if (!seen.has(post.gbp_campaign_id)) {
      seen.set(post.gbp_campaign_id, { id: post.gbp_campaign_id, name: post.campaign_name || 'Unnamed' })
    }
  })
  return [...seen.values()]
})

const filteredPosts = computed(() => {
  if (!filterCampaignId.value) return posts.value
  return posts.value.filter((p) => p.gbp_campaign_id === filterCampaignId.value)
})

const unscheduledPosts = computed(() => filteredPosts.value.filter((p) => !p.scheduled_date))

async function fetchPosts() {
  const monthStart = startOfMonth(currentMonth.value)
  const monthEnd = endOfMonth(currentMonth.value)
  const start = format(subWeeks(monthStart, 1), 'yyyy-MM-dd')
  const end = format(addWeeks(monthEnd, 1), 'yyyy-MM-dd')

  try {
    const { data } = await getLocationGbpPosts(props.locationId, { start_date: start, end_date: end })
    posts.value = data
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to load posts:', e)
  }
}

function prevMonth() {
  currentMonth.value = subMonths(currentMonth.value, 1)
}

function nextMonth() {
  currentMonth.value = addMonths(currentMonth.value, 1)
}

function goToday() {
  currentMonth.value = new Date()
}

function onFilterCampaign(campaignId) {
  filterCampaignId.value = campaignId
}

async function onDrop(postId, date) {
  const newDate = format(date, 'yyyy-MM-dd')
  const post = posts.value.find((p) => p.id === postId)
  if (!post || post.scheduled_date === newDate) return

  const oldDate = post.scheduled_date
  post.scheduled_date = newDate

  try {
    await updateGbpPost(postId, { gbp_post: { scheduled_date: newDate } })
  } catch (e) {
    post.scheduled_date = oldDate
    // eslint-disable-next-line no-console
    console.error('Failed to reschedule post:', e)
  }
}

async function onUnschedule(postId) {
  const post = posts.value.find((p) => p.id === postId)
  if (!post || !post.scheduled_date) return

  const oldDate = post.scheduled_date
  post.scheduled_date = null

  try {
    await updateGbpPost(postId, { gbp_post: { scheduled_date: '' } })
  } catch (e) {
    post.scheduled_date = oldDate
    // eslint-disable-next-line no-console
    console.error('Failed to unschedule post:', e)
  }
}

function onClickPost(post) {
  selectedPost.value = post
}

function onEditPost(post) {
  selectedPost.value = null
  slidePanel.value.open({
    component: GbpPostEditor,
    props: { post },
    events: {
      updated: () => {
        slidePanel.value.close()
        fetchPosts()
      }
    }
  })
}

async function onRegeneratePost(post) {
  selectedPost.value = null
  try {
    await regenerateGbpPost(post.id)
    fetchPosts()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to regenerate post:', e)
  }
}

function onClickDay(date) {
  slidePanel.value.open({
    component: QuickCreateForm,
    props: { locationId: props.locationId, date },
    events: {
      created: () => {
        slidePanel.value.close()
        fetchPosts()
      }
    }
  })
}

watch(currentMonth, fetchPosts)
onMounted(fetchPosts)
</script>
